import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { adminApi } from '@/api';
import type { PromoCode } from '@/api/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { formatDateTime, formatRub } from '@/lib/utils';

type Form = {
  id?: number;
  code: string;
  description: string;
  type: 'percent' | 'fixed';
  value: number;          // для fixed - рубли (не копейки)
  minAmountRub: string;   // строкой чтобы можно было пусто оставить
  maxUses: string;
  maxUsesPerUser: string;
  validFromLocal: string; // datetime-local input
  validUntilLocal: string;
  isActive: boolean;
};

const empty = (): Form => ({
  code: '',
  description: '',
  type: 'percent',
  value: 10,
  minAmountRub: '',
  maxUses: '',
  maxUsesPerUser: '',
  validFromLocal: '',
  validUntilLocal: '',
  isActive: true,
});

function toIso(local: string): string | null {
  if (!local) return null;
  // datetime-local не имеет TZ - берём как локальное.
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function fromIso(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // Приводим к YYYY-MM-DDTHH:mm
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function AdminPromos() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['admin', 'promos'], queryFn: adminApi.promos.list });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Form>(empty());

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        code: form.code.trim().toUpperCase(),
        description: form.description.trim() || null,
        type: form.type,
        value: form.type === 'fixed' ? Math.round(form.value * 100) : form.value,
        minAmountMinor: form.minAmountRub ? Math.round(Number(form.minAmountRub) * 100) : null,
        maxUses: form.maxUses ? Number(form.maxUses) : null,
        maxUsesPerUser: form.maxUsesPerUser ? Number(form.maxUsesPerUser) : null,
        validFrom: toIso(form.validFromLocal),
        validUntil: toIso(form.validUntilLocal),
        isActive: form.isActive,
      };
      return form.id ? adminApi.promos.update(form.id, payload as any) : adminApi.promos.create(payload as any);
    },
    onSuccess: () => {
      toast.success('Сохранено');
      qc.invalidateQueries({ queryKey: ['admin', 'promos'] });
      setOpen(false);
    },
    onError: () => toast.error('Не удалось сохранить'),
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminApi.promos.remove(id),
    onSuccess: () => {
      toast.success('Удалено');
      qc.invalidateQueries({ queryKey: ['admin', 'promos'] });
    },
  });

  function openEdit(p: PromoCode) {
    setForm({
      id: p.id,
      code: p.code,
      description: p.description ?? '',
      type: p.type,
      value: p.type === 'fixed' ? p.value / 100 : p.value,
      minAmountRub: p.minAmountMinor != null ? String(p.minAmountMinor / 100) : '',
      maxUses: p.maxUses != null ? String(p.maxUses) : '',
      maxUsesPerUser: p.maxUsesPerUser != null ? String(p.maxUsesPerUser) : '',
      validFromLocal: fromIso(p.validFrom),
      validUntilLocal: fromIso(p.validUntil),
      isActive: p.isActive,
    });
    setOpen(true);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-extrabold text-eol-white">Промокоды</h1>
        <Button
          onClick={() => {
            setForm(empty());
            setOpen(true);
          }}
          className="gap-2"
        >
          <Plus className="h-4 w-4" /> Создать
        </Button>
      </div>

      {!data?.length ? (
        <p className="text-eol-desc">Промокоды ещё не созданы.</p>
      ) : (
        <div className="space-y-3">
          {data.map((p) => (
            <Card key={p.id}>
              <CardContent className="pt-4 pb-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-mono font-bold text-eol-pink">{p.code}</p>
                    <Badge variant="secondary">
                      {p.type === 'percent' ? `−${p.value}%` : `−${formatRub(p.value)}`}
                    </Badge>
                    {!p.isActive && <Badge variant="danger">Неактивен</Badge>}
                  </div>
                  {p.description && (
                    <p className="text-sm text-eol-desc mt-1">{p.description}</p>
                  )}
                  <p className="text-xs text-eol-desc mt-1">
                    Использовано: {p.usesCount}
                    {p.maxUses != null && ` / ${p.maxUses}`}
                    {p.maxUsesPerUser != null && ` · до ${p.maxUsesPerUser} на пользователя`}
                    {p.minAmountMinor != null && ` · от ${formatRub(p.minAmountMinor)}`}
                  </p>
                  {(p.validFrom || p.validUntil) && (
                    <p className="text-xs text-eol-desc">
                      {p.validFrom && `с ${formatDateTime(p.validFrom)}`}
                      {p.validUntil && ` по ${formatDateTime(p.validUntil)}`}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button size="icon" variant="secondary" onClick={() => openEdit(p)} title="Редактировать">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => { if (confirm(`Удалить промокод «${p.code}»?`)) remove.mutate(p.id); }}
                    title="Удалить"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Редактировать промокод' : 'Новый промокод'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Код (латиница, цифры)" className="col-span-2">
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="WINTER25"
                className="uppercase font-mono"
              />
            </Field>
            <Field label="Описание (необязательно)" className="col-span-2">
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Зимняя акция"
              />
            </Field>
            <Field label="Тип">
              <select
                className="h-10 rounded-md border border-white/10 bg-eol-black/40 px-3 text-sm text-eol-white w-full"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value as 'percent' | 'fixed' })}
              >
                <option value="percent">Процент</option>
                <option value="fixed">Фиксированная сумма</option>
              </select>
            </Field>
            <Field label={form.type === 'percent' ? 'Значение, % (1..100)' : 'Значение, ₽'}>
              <Input
                type="number"
                value={form.value}
                onChange={(e) => setForm({ ...form, value: Number(e.target.value) })}
              />
            </Field>
            <Field label="Мин. сумма заказа, ₽ (пусто = нет)">
              <Input
                value={form.minAmountRub}
                onChange={(e) => setForm({ ...form, minAmountRub: e.target.value })}
              />
            </Field>
            <Field label="Лимит активаций (всего, пусто = ∞)">
              <Input
                value={form.maxUses}
                onChange={(e) => setForm({ ...form, maxUses: e.target.value })}
              />
            </Field>
            <Field label="Лимит на пользователя (пусто = ∞)">
              <Input
                value={form.maxUsesPerUser}
                onChange={(e) => setForm({ ...form, maxUsesPerUser: e.target.value })}
              />
            </Field>
            <Field label="Активен с">
              <Input
                type="datetime-local"
                value={form.validFromLocal}
                onChange={(e) => setForm({ ...form, validFromLocal: e.target.value })}
              />
            </Field>
            <Field label="Активен до">
              <Input
                type="datetime-local"
                value={form.validUntilLocal}
                onChange={(e) => setForm({ ...form, validUntilLocal: e.target.value })}
              />
            </Field>
            <Field label="Статус" className="col-span-2">
              <label className="flex items-center gap-2 text-eol-white">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
                Активен
              </label>
            </Field>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>Отмена</Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending}>Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={`flex flex-col gap-1.5 ${className ?? ''}`}>
      <span className="text-xs uppercase font-semibold tracking-wider text-eol-desc">{label}</span>
      {children}
    </label>
  );
}
